
import { useState, useMemo } from 'react';
import type { NeighborhoodDetail } from '../../../../mocks/neighborhoodDetails';
import BusinessCard from './BusinessCard';

interface BusinessListingsProps {
  neighborhood: NeighborhoodDetail;
}

const categoryLabels: Record<string, { label: string; icon: string }> = {
  all: { label: 'All Businesses', icon: 'ri-layout-grid-line' },
  dining: { label: 'Dining', icon: 'ri-restaurant-line' },
  shopping: { label: 'Shopping', icon: 'ri-shopping-bag-line' },
  entertainment: { label: 'Entertainment', icon: 'ri-film-line' },
  wellness: { label: 'Wellness', icon: 'ri-heart-pulse-line' },
  services: { label: 'Services', icon: 'ri-briefcase-line' },
};

export default function BusinessListings({ neighborhood }: BusinessListingsProps) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');

  const availableCategories = useMemo(() => {
    const cats = new Set(neighborhood.businesses.map((b) => b.category));
    return ['all', ...Array.from(cats)];
  }, [neighborhood.businesses]);

  const filteredBusinesses = useMemo(() => {
    let results = [...neighborhood.businesses];

    if (activeCategory !== 'all') {
      results = results.filter((b) => b.category === activeCategory);
    }

    switch (sortBy) {
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        results.sort((a, b) => b.reviews - a.reviews);
        break;
      case 'name':
        results.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'featured':
      default:
        results.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.rating - a.rating;
        });
        break;
    }

    return results;
  }, [neighborhood.businesses, activeCategory, sortBy]);

  return (
    <section id="businesses" className="py-16 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Local Businesses
            </h2>
            <p className="text-gray-500 text-sm">
              Discover {neighborhood.businessCount}+ businesses in {neighborhood.name}
            </p>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
            >
              <option value="featured">Featured First</option>
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviews</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {availableCategories.map((cat) => {
            const info = categoryLabels[cat] || { label: cat, icon: 'ri-store-line' };
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-teal-600 text-white shadow-lg shadow-teal-200'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <i className={`${info.icon} w-4 h-4 flex items-center justify-center`}></i>
                {info.label}
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        <p className="text-sm text-gray-500 mb-6">
          Showing <strong className="text-gray-900">{filteredBusinesses.length}</strong> business{filteredBusinesses.length !== 1 ? 'es' : ''}
        </p>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBusinesses.map((business) => (
            <BusinessCard
              key={business.id}
              business={business}
              accentColor={neighborhood.accentColor}
            />
          ))}
        </div>

        {filteredBusinesses.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-store-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm mb-1">No businesses found in this category</p>
            <button
              onClick={() => setActiveCategory('all')}
              className="text-teal-600 text-sm font-semibold hover:text-teal-700 cursor-pointer whitespace-nowrap"
            >
              View all businesses
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
